import type { ListBuilderConfiguration } from '../types';

/**
 * Item Lot Item Embedded list configuration
 * Shows lots for a specific item
 * Converted from JSON to TypeScript for type safety
 */
export const ITEM_LOT_ITEM_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-item-lots-item-embedded',
			cacheTTL: 600000,
			name: 'Item Lots',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'lot-number',
					fields: [
						{
							id: 'lot-number-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lotNumber' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'lb1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'external-id-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'externalId' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Lot Number' }],
					minWidth: 150,
					sortable: true,
					alignment: 'left',
					sortFields: [{ direction: 'ASC', fieldName: 'lotNumber' }],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'middle',
				},
				{
					id: 'quantity-remaining',
					fields: [
						{
							id: 'quantity-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'quantityRemaining' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'number',
						},
					],
					headers: [{ id: 'h3', text: 'Qty Remaining' }],
					minWidth: 120,
					sortable: true,
					alignment: 'right',
					sortFields: [{ direction: 'DESC', fieldName: 'quantityRemaining' }],
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'dates',
					fields: [
						{
							id: 'expiration-date-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'expirationDate' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'date',
						},
						{
							id: 'lb1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'production-date-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'productionDate' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h4', text: 'Expiration / Production' }],
					minWidth: 130,
					sortable: true,
					alignment: 'center',
					sortFields: [{ direction: 'ASC', fieldName: 'expirationDate' }],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'middle',
				},
				{
					id: 'warehouse-info',
					fields: [
						{
							id: 'warehouse-name-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'warehouseName' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h6', text: 'Warehouse' }],
					minWidth: 120,
					sortable: true,
					alignment: 'left',
					sortFields: [{ direction: 'ASC', fieldName: 'warehouseName' }],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'middle',
				},
				{
					id: 'actions-column',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'editLot',
									variant: 'primary',
									visibility: 'visible',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'deleteLot',
									variant: 'danger',
									visibility: 'visible',
								},
							],
						},
					],
					headers: [{ id: 'h-actions', text: '' }],
					minWidth: 50,
					sortable: false,
					alignment: 'right',
					widthConfig: { mode: 'fixed', width: 60 },
					verticalAlignment: 'middle',
				},
			],
			description: 'Lots for this item',
			defaultSortId: 'expiration-date',
			requiredFields: [
				'itemLot.id',
				'itemLot.lotNumber',
				'itemLot.itemId',
				'itemLot.warehouseId',
				'itemLot.quantityRemaining',
				'itemLot.expirationDate',
			],
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [
			'itemLotKeywords',
			'itemLotExpirationDate',
			'itemLotProductionDate',
			'itemLotWarehouse',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/lots/:id',
			objectLabel: 'Item Lot',
			objectTable: 'item_lot',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'lotNumber', fieldLabel: 'Lot Number' },
				{ fieldName: 'quantityRemaining', fieldLabel: 'Quantity Remaining' },
				{ fieldName: 'expirationDate', fieldLabel: 'Expiration Date' },
				{ fieldName: 'productionDate', fieldLabel: 'Production Date' },
				{ fieldName: 'externalId', fieldLabel: 'External ID' },
				{ fieldName: '_created_date', fieldLabel: 'Created Date' },
			],
		},
	],
	tableSorts: [
		{
			id: 'lot-number',
			name: 'Lot Number',
			fields: [{ direction: 'ASC', fieldName: 'lotNumber' }],
			description: 'Sort by lot number',
		},
		{
			id: 'expiration-date',
			name: 'Expiration Date',
			fields: [{ direction: 'ASC', fieldName: 'expirationDate' }],
			description: 'Sort by expiration date (earliest first)',
		},
		{
			id: 'quantity',
			name: 'Quantity',
			fields: [{ direction: 'DESC', fieldName: 'quantityRemaining' }],
			description: 'Sort by quantity remaining (highest first)',
		},
		{
			id: 'production-date',
			name: 'Production Date',
			fields: [{ direction: 'DESC', fieldName: 'productionDate' }],
			description: 'Sort by production date (newest first)',
		},
	],
};
